import { useState, useEffect, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import useProfileId from "hooks/useProfile";
import { logError } from "util/logging";

const useUserAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const { profileId } = useProfileId();

  // Only fetch when we have an authenticated session
  const [ready, setReady] = useState(false);

  // Prevent duplicate calls (React StrictMode, quick re-renders)
  const inFlight = useRef(false);

  // Establish auth readiness and react to sign in/out
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setAccounts([]);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Fetch accounts once authed and profileId is known
  useEffect(() => {
    if (!ready || !profileId) return;
    if (inFlight.current) return;
    inFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check session
        const userAccountsRequest = await API.get(
          "AccountGatewayApi",
          "/accounts"
        );

        if (cancelled) return;

        const accountsToSet = (userAccountsRequest || []).reduce((acc, account) => {
          acc.push({
            accountId: account.id,
            name: account.name,
            brand: "",
            role: "",
            accountProfileId: "",
          });
          return acc;
        }, []);

        setAccounts(accountsToSet);
      } catch (err) {
        console.error("userAccountsRequest error", err);
      } finally {
        inFlight.current = false;
      }
    })().catch((err) => {
      // catch any top-level async errors to avoid unhandled rejections
      logError("useUserAccounts fetchData error", err);
      inFlight.current = false;
    });

    return () => {
      cancelled = true;
    };
  }, [ready, profileId]);

  return { accounts };
};

export default useUserAccounts;
